import { isCookieConsentGranted } from '~/utils/cookies';
import { isReactNativeWebView } from '~/utils/isReactNativeWebView';

import { EventDeduplicator } from './EventDeduplicator';
import { EVENT_REGISTRY } from './eventRegistry';
import type { AnalyticsEvent, EventPayloadMap } from './events';
import type {
  AnalyticsProvider,
  EventRegistryEntry,
  ProviderName,
} from './types';

/**
 * Max number of events buffered before `initialize()` runs. Provider setup is
 * deferred to browser idle for Web Vitals, so early `track()` calls are queued
 * here and flushed once providers are ready — the cap guards against unbounded
 * growth if initialization never happens.
 */
const MAX_PENDING_EVENTS = 100;

class AnalyticsServiceImpl {
  private providers = new Map<ProviderName, AnalyticsProvider>();
  private batchQueue = new Map<string, Record<string, unknown>[]>();
  private deduplicator = new EventDeduplicator();
  private initialized = false;
  private pendingEvents: Array<{
    event: AnalyticsEvent;
    payload: Record<string, unknown>;
  }> = [];
  /**
   * User id captured before `initialize()` ran. `undefined` means nothing was
   * captured — distinct from `null`, which is an explicit sign-out.
   */
  private pendingUserId: string | null | undefined;
  /**
   * Id that a consent-gated provider refused at the time it was set, kept so
   * acceptance later in the session can still be honoured. `undefined` means
   * nothing is outstanding.
   */
  private withheldUserId: string | null | undefined;
  /** Last id the providers actually received, to skip repeat announcements. */
  private appliedUserId: string | null | undefined;

  registerProvider(provider: AnalyticsProvider): void {
    this.providers.set(provider.name, provider);
  }

  initialize(): void {
    if (this.initialized) return;
    this.deduplicator.start();
    for (const provider of this.providers.values()) {
      if (provider.isAvailable()) {
        provider.initialize();
      }
    }
    this.initialized = true;

    if (this.pendingUserId !== undefined) {
      const userId = this.pendingUserId;
      this.pendingUserId = undefined;
      this.applyUserId(userId);
    }

    const pending = this.pendingEvents;
    this.pendingEvents = [];
    for (const { event, payload } of pending) {
      this.dispatch(event, payload);
    }
  }

  /**
   * Identifies the customer to every provider that supports it, so the same
   * person is one user rather than one per device.
   *
   * Buffered until `initialize()` because provider setup is deferred to browser
   * idle — a session restored on load identifies the customer long before the
   * providers exist.
   *
   * Ignored on the server: this service is a module singleton shared by every
   * request, and no provider reports from there anyway, so a buffered id would
   * only be one customer's identifier sitting in memory across other people's
   * requests.
   */
  setUserId(userId: string | null): void {
    if (import.meta.env.SSR) {
      return;
    }

    // Every profile save and every personal-data refresh re-announces the same
    // customer; there is nothing for a provider to do with the repeat.
    if (userId === this.appliedUserId) {
      return;
    }

    if (!this.initialized) {
      this.pendingUserId = userId;
      return;
    }
    this.applyUserId(userId);
  }

  private applyUserId(userId: string | null): void {
    let withheld = false;

    for (const provider of this.providers.values()) {
      if (!provider.setUserId || !provider.isAvailable()) continue;
      if (provider.requiresConsent && !this.isConsentGranted()) {
        withheld = true;
        continue;
      }

      provider.setUserId(userId);
    }

    this.withheldUserId = withheld ? userId : undefined;
    this.appliedUserId = withheld ? undefined : userId;
  }

  /**
   * Re-offers an id that consent was missing for when it was first set.
   *
   * Sign-in can precede the cookie banner, and nothing announces acceptance.
   * Without this the customer stays anonymous in GA for the rest of the session
   * even though their events are now flowing — dropping the id on the floor at
   * the moment consent is refused makes it unrecoverable.
   */
  private retryWithheldUserId(): void {
    if (this.withheldUserId === undefined || !this.isConsentGranted()) {
      return;
    }
    this.applyUserId(this.withheldUserId);
  }

  track<E extends AnalyticsEvent>(
    event: E,
    ...args: EventPayloadMap[E] extends undefined
      ? []
      : [payload: EventPayloadMap[E]]
  ): void {
    const payload = (args[0] ?? {}) as Record<string, unknown>;

    if (!this.initialized) {
      // Nothing on the server ever flushes this buffer — `initialize()` runs
      // from the client entry alone — so anything pushed there is one visitor's
      // payloads, `customer_id` included, retained in a process shared with
      // every other request.
      if (
        !import.meta.env.SSR &&
        this.pendingEvents.length < MAX_PENDING_EVENTS
      ) {
        this.pendingEvents.push({ event, payload });
      }
      return;
    }

    this.dispatch(event, payload);
  }

  private dispatch(
    event: AnalyticsEvent,
    payload: Record<string, unknown>,
  ): void {
    const entry = EVENT_REGISTRY[event];
    if (!entry) {
      return;
    }

    this.retryWithheldUserId();

    if (this.deduplicator.isDuplicate(event, payload)) {
      return;
    }

    for (const providerName of entry.providers) {
      const provider = this.providers.get(providerName);
      if (!provider || !provider.isAvailable()) continue;

      if (this.isBlockedByConsent(provider, entry)) continue;

      const resolvedEventName =
        entry.eventNames?.[providerName] ?? (event as string);
      const resolvedPayload = entry.transformers?.[providerName]
        ? entry.transformers[providerName]!(payload)
        : payload;

      provider.track(resolvedEventName, resolvedPayload);
    }
  }

  addToBatch<E extends AnalyticsEvent>(
    event: E,
    payload: EventPayloadMap[E] & Record<string, unknown>,
  ): void {
    const key = event as string;
    const queue = this.batchQueue.get(key) ?? [];
    queue.push(payload);
    this.batchQueue.set(key, queue);
  }

  flush(event: AnalyticsEvent): void {
    const key = event as string;
    const queue = this.batchQueue.get(key);
    if (!queue?.length) return;

    const entry = EVENT_REGISTRY[key];
    if (!entry) return;

    for (const providerName of entry.providers) {
      const provider = this.providers.get(providerName);
      if (!provider || !provider.isAvailable()) continue;
      if (this.isBlockedByConsent(provider, entry)) continue;

      const resolvedEventName =
        entry.eventNames?.[providerName] ?? (event as string);
      const batchTransformer = entry.batchTransformers?.[providerName];

      if (batchTransformer) {
        for (const payload of batchTransformer(queue)) {
          provider.track(resolvedEventName, payload);
        }
      } else if (provider.trackBatch) {
        provider.trackBatch(resolvedEventName, queue);
      } else {
        for (const item of queue) {
          provider.track(resolvedEventName, item);
        }
      }
    }

    this.batchQueue.set(key, []);
  }

  destroy(): void {
    this.deduplicator.destroy();
    for (const provider of this.providers.values()) {
      provider.destroy?.();
    }
    this.providers.clear();
    this.batchQueue.clear();
    this.pendingEvents = [];
    this.pendingUserId = undefined;
    this.withheldUserId = undefined;
    this.appliedUserId = undefined;
    this.initialized = false;
  }

  private isBlockedByConsent(
    provider: AnalyticsProvider,
    entry: EventRegistryEntry,
  ): boolean {
    const requiresConsent = entry.requiresConsent ?? provider.requiresConsent;
    return requiresConsent && !this.isConsentGranted();
  }

  isConsentGranted(): boolean {
    if (isReactNativeWebView()) return true;
    return isCookieConsentGranted();
  }
}

export const analyticsService = new AnalyticsServiceImpl();
